import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

// Definindo os tipos para os dados
type Company = {
  name: string // Nome do cliente
  boletoValue: number // Valor do boleto
  dueDate: string // Dia do vencimento
  creationDate: string // Dia da criação
}

type MonthlyDetails = {
  A_RECEBER: Company[]
  EM_PROCESSAMENTO: Company[]
  ATRASADO: Company[]
  RECEBIDO: Company[]
  EXPIRADO: Company[]
}

const monthlyDetails: Record<string, MonthlyDetails> = {
  Janeiro: {
    A_RECEBER: [
      { name: 'Empresa A', boletoValue: 1000, dueDate: '2024-01-15', creationDate: '2024-01-01' },
      { name: 'Empresa B', boletoValue: 1500, dueDate: '2024-01-20', creationDate: '2024-01-05' }
    ],
    EM_PROCESSAMENTO: [{ name: 'Empresa C', boletoValue: 2000, dueDate: '2024-01-25', creationDate: '2024-01-10' }],
    ATRASADO: [{ name: 'Empresa D', boletoValue: 1200, dueDate: '2024-01-05', creationDate: '2024-01-01' }],
    RECEBIDO: [{ name: 'Empresa E', boletoValue: 2500, dueDate: '2024-01-10', creationDate: '2024-01-01' }],
    EXPIRADO: [{ name: 'Empresa F', boletoValue: 1800, dueDate: '2023-12-31', creationDate: '2023-12-01' }]
  },
  Fevereiro: {
    A_RECEBER: [
      { name: 'Empresa A', boletoValue: 10600, dueDate: '2024-01-15', creationDate: '2024-01-01' },
      { name: 'Empresa B', boletoValue: 1500, dueDate: '2024-01-20', creationDate: '2024-01-05' }
    ],
    EM_PROCESSAMENTO: [{ name: 'Empresa C', boletoValue: 20000, dueDate: '2024-01-25', creationDate: '2024-01-10' }],
    ATRASADO: [{ name: 'Empresa D', boletoValue: 30200, dueDate: '2024-01-05', creationDate: '2024-01-01' }],
    RECEBIDO: [{ name: 'Empresa E', boletoValue: 10500, dueDate: '2024-01-10', creationDate: '2024-01-01' }],
    EXPIRADO: [{ name: 'Empresa F', boletoValue: 20000, dueDate: '2023-12-31', creationDate: '2023-12-01' }]
  }
}

// Função para consolidar todos os meses
const getAllYearDetails = () => {
  const allDetails: MonthlyDetails = { A_RECEBER: [], EM_PROCESSAMENTO: [], ATRASADO: [], RECEBIDO: [], EXPIRADO: [] }

  Object.values(monthlyDetails).forEach(month => {
    allDetails.RECEBIDO.push(...month.RECEBIDO)
    allDetails.A_RECEBER.push(...month.A_RECEBER)
    allDetails.EM_PROCESSAMENTO.push(...month.EM_PROCESSAMENTO)
    allDetails.ATRASADO.push(...month.ATRASADO)
    allDetails.EXPIRADO.push(...month.EXPIRADO)
  })

  return allDetails
}

const allYearDetails = getAllYearDetails()

type StatusKey = keyof MonthlyDetails

const StatCard = ({
  titulo,
  valor,
  valorReais,
  cor,
  aoClicar,
  selecionado
}: {
  titulo: string
  valor: number
  valorReais: number
  cor: string
  aoClicar: () => void
  selecionado: boolean
}) => (
  <div
    className={`bg-background text-foreground p-4 rounded-lg shadow cursor-pointer hover:shadow-md transition-shadow ${
      selecionado ? 'ring-2 ring-offset-2 ring-' + cor : ''
    }`}
    onClick={aoClicar}
  >
    <h3 className="text-lg font-semibold text-foreground">{titulo}</h3>
    <p className="text-3xl font-bold" style={{ color: cor }}>
      {valor}
    </p>
    <p className="text-sm text-foreground">R$ {valorReais.toLocaleString('pt-BR')}</p>
  </div>
)

export default function BoletoResumo() {
  const [statusSelecionado, setStatusSelecionado] = useState<StatusKey | null>(null)
  const [mesSelecionado, setMesSelecionado] = useState<string>('Todos os meses')

  const detalhes = mesSelecionado === 'Todos os meses' ? allYearDetails : monthlyDetails[mesSelecionado]

  const handleStatusClick = (status: StatusKey) => {
    setStatusSelecionado(atual => (atual === status ? null : status))
  }

  return (
    <div className="space-y-8">
      {/* Seletor de mês */}
      <select
        className="p-2 rounded text-foreground bg-background shadow-md "
        value={mesSelecionado}
        onChange={e => setMesSelecionado(e.target.value)}
      >
        <option value="Todos os meses">Todos os meses</option>
        {Object.keys(monthlyDetails).map(mes => (
          <option key={mes} value={mes}>
            {mes}
          </option>
        ))}
      </select>

      <Card className="shadow-md">
        <CardHeader>
          <CardTitle className="text-foreground ">Boletos - {mesSelecionado}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 shadow-md">
            <StatCard
              titulo="Recebido"
              valor={detalhes.RECEBIDO.length}
              valorReais={detalhes.RECEBIDO.reduce((sum, item) => sum + item.boletoValue, 0)}
              cor="#2ecc71"
              aoClicar={() => handleStatusClick('RECEBIDO')}
              selecionado={statusSelecionado === 'RECEBIDO'}
            />
            <StatCard
              titulo="A Receber"
              valor={detalhes.A_RECEBER.length}
              valorReais={detalhes.A_RECEBER.reduce((sum, item) => sum + item.boletoValue, 0)}
              cor="#3498db"
              aoClicar={() => handleStatusClick('A_RECEBER')}
              selecionado={statusSelecionado === 'A_RECEBER'}
            />
            <StatCard
              titulo="Em Processamento"
              valor={detalhes.EM_PROCESSAMENTO.length}
              valorReais={detalhes.EM_PROCESSAMENTO.reduce((sum, item) => sum + item.boletoValue, 0)}
              cor="#f39c12"
              aoClicar={() => handleStatusClick('EM_PROCESSAMENTO')}
              selecionado={statusSelecionado === 'EM_PROCESSAMENTO'}
            />
            <StatCard
              titulo="Atrasado"
              valor={detalhes.ATRASADO.length}
              valorReais={detalhes.ATRASADO.reduce((sum, item) => sum + item.boletoValue, 0)}
              cor="#e74c3c"
              aoClicar={() => handleStatusClick('ATRASADO')}
              selecionado={statusSelecionado === 'ATRASADO'}
            />

            <StatCard
              titulo="Cobrança"
              valor={detalhes.EXPIRADO.length}
              valorReais={detalhes.EXPIRADO.reduce((sum, item) => sum + item.boletoValue, 0)}
              cor="#e67e22"
              aoClicar={() => handleStatusClick('EXPIRADO')}
              selecionado={statusSelecionado === 'EXPIRADO'}
            />
          </div>
        </CardContent>
      </Card>

      {/* Detalhes Expandidos */}
      {statusSelecionado && detalhes[statusSelecionado]?.length > 0 && (
        <Card className="mt-4">
          <CardHeader>
            <CardTitle>Detalhes: {statusSelecionado}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {detalhes[statusSelecionado].map((item, index) => (
                <div key={index} className="bg-gray-100 p-4 rounded-lg shadow">
                  <h3 className="text-lg font-semibold">{item.name}</h3>
                  <p className="text-sm">Valor do Boleto: R$ {item.boletoValue.toLocaleString('pt-BR')}</p>
                  <p className="text-sm">Vencimento: {item.dueDate}</p>
                  <p className="text-sm">Criação: {item.creationDate}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {statusSelecionado && detalhes[statusSelecionado]?.length === 0 && (
        <p className="text-red-500 mt-4">Nenhum detalhe disponível para esta categoria.</p>
      )}
    </div>
  )
}
